import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Panel,
  StatusBadge,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Check, RunModes, Steps } from '../components/2c-parts.js';
import type { StepState } from '../components/2c-parts.js';
import type { Run } from './SeImport.js';
import '../screens-2c.css';

const STATUS: Record<Run['status'], { label: string; tone: Tone }> = {
  queued: { label: 'čeká na spuštění', tone: 'neu' },
  running: { label: 'běží', tone: 'acc' },
  done: { label: 'hotovo', tone: 'ok' },
  failed: { label: 'selhal', tone: 'bad' },
  cancelled: { label: 'zrušen', tone: 'warn' },
};

const COUNT_LABEL: Record<string, string> = {
  sentCampaigns: 'Kampaní ke čtení',
  sentEvents: 'Událostí přečteno',
  sentWouldCreate: 'Záznamů by vzniklo',
  sentCreated: 'Záznamů založeno',
  sentAlready: 'Už v hubu (přeskočeno)',
  sentHubOwn: 'Odeslal hub sám (spárováno)',
  sentNoContact: 'Bez kontaktu v hubu',
  sentTest: 'Zkušební odeslání (nezakládají se)',
  sentForeignWouldCreate: 'z toho cizí kampaně (by vzniklo)',
  sentForeignCreated: 'z toho cizí kampaně (založeno)',
  reactOpened: 'Otevření',
  reactClicked: 'Kliknutí',
  reactBounced: 'Nedoručeno',
  reactUnsubscribed: 'Odhlášení',
  sentEventsStored: 'Reakcí uloženo',
  sentEventsDuplicate: 'Reakcí už známých',
  sentCampaignErrors: 'Kampaní s chybou',
};

/** `YYYY-MM-DD` z výběru data → začátek, resp. konec dne (UTC); prázdné = výchozí okno. */
const dayStart = (v: string): string | undefined =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00.000Z` : undefined;
const dayEnd = (v: string): string | undefined =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T23:59:59.000Z` : undefined;

interface SentReport {
  sentByTag?: Record<string, { n: number; first: string; last: string }>;
  sentSample?: Array<{ email: string; tag: string; at: string; foreign: boolean }>;
  sentLimitReached?: boolean;
  sentWindow?: { from: string; to: string };
}

/**
 * Krok „3. Odeslané e-maily (historie)“ v Import ze SmartEmailingu (D-223). Jen čtení ze SE;
 * v hubu vzniknou záznamy ve stavu „importováno“, které se nikdy neodešlou.
 */
export function SeSentHistory({
  brand,
  runs,
  active,
  canWrite,
  tz,
  envName,
}: {
  brand: string;
  runs: Run[];
  /** Pro značku už běží nějaký import (server dovolí jen jeden). */
  active: boolean;
  canWrite: boolean;
  tz: string;
  envName: string;
}): JSX.Element {
  const client = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState('');
  const [foreign, setForeign] = useState(true);
  const [foreignShop, setForeignShop] = useState(false);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const limitValue = Number.parseInt(limit, 10);
  const limitInvalid = limit.trim() !== '' && !(limitValue >= 1);
  const rangeInvalid = from !== '' && to !== '' && from > to;

  const start = useMutation({
    mutationFn: (input: { mode: 'dry_run' | 'apply'; word?: string }) =>
      api.post('/api/provider-import/runs', {
        brand,
        mode: input.mode,
        confirmation: input.word,
        options: {
          sentOnly: true,
          sentForeign: foreign,
          sentForeignTransactional: foreignShop,
          ...(dayStart(from) ? { sentFrom: dayStart(from) } : {}),
          ...(dayEnd(to) ? { sentTo: dayEnd(to) } : {}),
          ...(limit.trim() !== '' && limitValue >= 1 ? { sentLimit: limitValue } : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setAsking(false);
      void client.invalidateQueries({ queryKey: ['se-import'] });
    },
    onError: (err: Error) => {
      setAsking(false);
      setError(err.message);
    },
  });

  const shown = runs[0];
  const dryDone = runs.some((r) => r.mode === 'dry_run' && r.status === 'done');
  const applyDone = runs.some((r) => r.mode === 'apply' && r.status === 'done');
  const step = (done: boolean, now: boolean): StepState => (done ? 'done' : now ? 'now' : 'todo');
  const blocked = active || limitInvalid || rangeInvalid;
  const reason = active
    ? 'Pro tuhle značku už import běží.'
    : limitInvalid || rangeInvalid
      ? 'Opravte hodnoty označené jako chybné.'
      : undefined;
  const report = (shown?.report ?? {}) as SentReport;
  const tags = Object.entries(report.sentByTag ?? {}).sort((a, b) => b[1].n - a[1].n);

  return (
    <Panel
      title="3. Odeslané e-maily (historie)"
      subtitle="Zjistí, jaké e-maily SmartEmailing kontaktům už poslal, a uloží je jako záznamy „importováno“. Nikdy se znovu neodešlou."
    >
      <Steps
        label="Postup importu odeslaných e-mailů"
        steps={[
          {
            title: 'Zkouška nanečisto',
            text: 'Spočítá kampaně a záznamy. V hubu se nic nezmění.',
            state: step(dryDone, true),
          },
          {
            title: 'Ostrý zápis',
            text: 'Uloží historii (zprávy ve stavu „importováno“) a jejich reakce.',
            state: step(applyDone, dryDone),
          },
        ]}
      />
      <RunModes
        dry="Hub přečte seznam kampaní a jejich události a spočítá, kolik záznamů by vzniklo. Nic nezapíše; zkoušku lze opakovat."
        live={`Uloží záznamy o odeslaných e-mailech do databáze prostředí ${envName}. Do SmartEmailingu se nezapisuje nic a žádný e-mail se neodešle.`}
      />
      <div className="help">
        <p>
          Záznamy se vážou na kontakty, které už hub zná, proto nejdřív proběhne import kontaktů
          (krok 2 výše). Hub čte jen kampaně se značkou <code>kód-značka</code> (např.{' '}
          <code>M01-TGP</code>) a „cizí“ marketingové kampaně bez kódu. Kontakty podle e-mailu
          včetně dřívějších adres. Do frekvenčního stropu se počítají jen marketingové zprávy s
          kódem hubu. Co SmartEmailing neumí (stáří statistik, newslettery a automatizace, obsah
          zpráv), je v dokumentaci jako otevřená otázka.
        </p>
      </div>
      {canWrite ? (
        <>
          <div className="s2c-form">
            <Field label="Odesláno od" hint="Bez data: posledních 12 měsíců.">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field
              label="Odesláno do"
              hint="Bez data: až dosud."
              error={rangeInvalid ? 'Datum „do“ nesmí být před datem „od“.' : null}
            >
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                aria-invalid={rangeInvalid}
              />
            </Field>
            <Field
              label="Nejvýš záznamů (strop pro zkoušku)"
              hint="Pro první zkoušku zkuste 50 až 200."
              error={limitInvalid ? 'Zadejte celé číslo od 1, nebo pole nechte prázdné.' : null}
            >
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                className="s2c-num"
                placeholder="bez omezení"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                aria-invalid={limitInvalid}
              />
            </Field>
          </div>
          <Check
            checked={foreign}
            onChange={setForeign}
            label="Zahrnout cizí marketingové kampaně"
            hint="Newslettery a jiné kampaně bez kódu hubu; uloží se jako „cizí kampaň“ bez kódu."
          />
          <Check
            checked={foreignShop}
            onChange={setForeignShop}
            label="Zahrnout transakční e-maily e-shopu"
            hint="Potvrzení objednávky a podobné. Jinak je do hubu posílá e-shop sám, takže je výchozí vypnuto."
          />
          <div className="s2c-actions">
            <button
              type="button"
              className="btn pri"
              disabled={blocked || start.isPending}
              onClick={() => start.mutate({ mode: 'dry_run' })}
            >
              Zkouška nanečisto
            </button>
            <button
              type="button"
              className="btn dng"
              disabled={blocked}
              onClick={() => setAsking(true)}
            >
              Importovat historii ostře
            </button>
            {reason ? <span className="s2c-reason">{reason}</span> : null}
          </div>
          {error ? <ErrorState error={new Error(error)} title="Akce se nepovedla" /> : null}
        </>
      ) : (
        <p className="note">Import může spustit jen správce.</p>
      )}

      {shown ? (
        <>
          <h3 className="s2c-sub">
            Poslední běh {shown.mode === 'dry_run' ? 'nanečisto' : 'ostrý'} z{' '}
            {dateTime(shown.createdAt, tz)}{' '}
            <StatusBadge tone={STATUS[shown.status].tone}>{STATUS[shown.status].label}</StatusBadge>
          </h3>
          {shown.status === 'running' || shown.status === 'queued' ? (
            <p className="s2c-runstate" role="status">
              Běh probíhá, stránka se sama obnovuje.
            </p>
          ) : null}
          {report.sentWindow ? (
            <p className="note">
              Okno odeslání: {dateTime(report.sentWindow.from, tz)} až{' '}
              {dateTime(report.sentWindow.to, tz)}
              {report.sentLimitReached ? ' · dosažen strop počtu záznamů' : ''}.
            </p>
          ) : null}
          {shown.error ? (
            <ErrorState error={new Error(shown.error)} title="Běh skončil chybou" />
          ) : null}
          {shown.errors.length > 0 ? (
            <ErrorState
              error={new Error(shown.errors.map((e) => e.message).join('\n'))}
              title="Některé kampaně se nepodařilo přečíst"
            />
          ) : null}
          <div className="tiles">
            {Object.entries(shown.counts)
              .filter(([key]) => key in COUNT_LABEL)
              .map(([key, value]) => (
                <Tile key={key} label={COUNT_LABEL[key] ?? key} value={num(value)} />
              ))}
          </div>
          {tags.length > 0 ? (
            <div>
              <h3 className="s2c-sub">Podle kampaní</h3>
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Značka kampaně</th>
                      <th className="n">Záznamů</th>
                      <th>Od</th>
                      <th>Do</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tags.map(([tag, v]) => (
                      <tr key={tag}>
                        <td>{tag}</td>
                        <td className="n">{num(v.n)}</td>
                        <td>{dateTime(v.first, tz)}</td>
                        <td>{dateTime(v.last, tz)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
          {report.sentSample?.length ? (
            <div>
              <h3 className="s2c-sub">Vzorek (maskované adresy)</h3>
              <div className="scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Příjemce</th>
                      <th>Kampaň</th>
                      <th>Odesláno</th>
                      <th>Druh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.sentSample.map((s, i) => (
                      <tr key={i}>
                        <td>{s.email}</td>
                        <td>{s.tag}</td>
                        <td>{dateTime(s.at, tz)}</td>
                        <td>{s.foreign ? 'cizí kampaň' : 'kampaň hubu'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState title="Historie odeslaných e-mailů zatím nebyla importována" icon="upload">
          Spusťte zkoušku nanečisto. Nic se tím nezmění a uvidíte, kolik záznamů by vzniklo.
        </EmptyState>
      )}

      <ConfirmDialog
        open={asking}
        title="Importovat historii odeslaných e-mailů ostře?"
        confirmLabel="Importovat ostře"
        danger
        requireWord="IMPORT"
        busy={start.isPending}
        onCancel={() => setAsking(false)}
        onConfirm={(word) => start.mutate({ mode: 'apply', word })}
      >
        <p>
          Do databáze prostředí <b>{envName}</b> se zapíšou záznamy o už odeslaných e-mailech a
          jejich reakce. Do SmartEmailingu se nezapisuje nic a žádný e-mail se neodešle ani znovu
          neodešle.
        </p>
        <p>
          {dryDone
            ? 'Zkouška nanečisto už proběhla.'
            : 'Zkouška nanečisto zatím neproběhla; doporučujeme ji pustit jako první.'}
        </p>
      </ConfirmDialog>
    </Panel>
  );
}
